import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import {
  Box,
  Typography,
  TextField,
  Button,
  Alert,
  Paper,
  Link,
  Stepper,
  Step,
  StepLabel,
  FormControl,
  FormControlLabel,
  Checkbox,
  MenuItem,
  Grid,
  CircularProgress,
} from '@mui/material';
import { Business, CreditCard, Person, ArrowBack, ArrowForward, Check, Image as ImageIcon, Delete } from '@mui/icons-material';

const API_BASE = process.env.REACT_APP_API_URL || 'http://localhost:8000';

const validateEmail = (email) => {
  const re = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
  return re.test(String(email || '').trim());
};

const validatePasswordStrength = (password) => {
  const minLength = 8;
  const hasSpecialChar = /[!@#$%^&*(),.?":{}|<>]/.test(password);
  return password.length >= minLength && hasSpecialChar;
};

const CompanyRegistration = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [currentStep, setCurrentStep] = useState(0);
  const [logoFile, setLogoFile] = useState(null);
  const [logoPreview, setLogoPreview] = useState(null);
  const [formData, setFormData] = useState({
    companyName: '',
    industry: '',
    companySize: '',
    address: '',
    city: '',
    state: '',
    zipCode: '',
    phone: '',
    companyEmail: '',
    website: '',
    selectedPlan: 'professional',
    firstName: '',
    lastName: '',
    username: '',
    adminEmail: '',
    password: '',
    confirmPassword: '',
    acceptTerms: false,
    acceptMarketing: false,
  });

  const [errors, setErrors] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);

  useEffect(() => {
    const planParam = searchParams.get('plan');
    const validPlans = ['starter', 'professional', 'enterprise'];
    if (planParam && validPlans.includes(planParam)) {
      setFormData((prev) => ({ ...prev, selectedPlan: planParam }));
    }
  }, [searchParams]);

  const industries = [
    'Accounting & Tax Services',
    'Bookkeeping Services',
    'Financial Advisory',
    'CPA Firm',
    'Tax Preparation',
    'Business Consulting',
    'Other',
  ];

  const companySizes = [
    '1-5 employees',
    '6-20 employees',
    '21-50 employees',
    '51-100 employees',
    '100+ employees',
  ];

  const plans = [
    {
      id: 'starter',
      name: 'Starter',
      price: '$99',
      period: '/month',
      description: 'Perfect for small firms getting started',
      features: ['Up to 5 team members', 'Basic client management', 'Standard reporting', 'Email support', 'Basic integrations'],
      popular: false,
    },
    {
      id: 'professional',
      name: 'Professional',
      price: '$199',
      period: '/month',
      description: 'Most popular for growing firms',
      features: ['Up to 20 team members', 'Advanced client management', 'Custom workflows', 'Priority support', 'API access', 'Advanced integrations'],
      popular: true,
    },
    {
      id: 'enterprise',
      name: 'Enterprise',
      price: 'Custom',
      period: '',
      description: 'For large firms with custom needs',
      features: ['Unlimited team members', 'Custom integrations', 'Dedicated support', 'On-premise options', 'Custom development', 'SLA guarantees'],
      popular: false,
    },
  ];

  const steps = [
    { label: 'Company Information', icon: <Business /> },
    { label: 'Choose Plan', icon: <CreditCard /> },
    { label: 'Create Admin Account', icon: <Person /> },
  ];

  const updateFormData = (field, value) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
    if (errors[field]) {
      setErrors((prev) => ({ ...prev, [field]: '' }));
    }
  };

  const handleLogoChange = (e) => {
    const file = e.target.files?.[0];
    if (file) {
      const validTypes = ['image/jpeg', 'image/png', 'image/gif', 'image/webp'];
      const maxSize = 2 * 1024 * 1024; // 2MB
      if (!validTypes.includes(file.type)) {
        setErrors((p) => ({ ...p, logo: 'Please upload a valid image (JPEG, PNG, GIF, or WebP)' }));
        return;
      }
      if (file.size > maxSize) {
        setErrors((p) => ({ ...p, logo: 'Image must be under 2MB' }));
        return;
      }
      setLogoFile(file);
      setLogoPreview(URL.createObjectURL(file));
      setErrors((p) => ({ ...p, logo: '' }));
    }
  };

  const handleLogoRemove = () => {
    if (logoPreview) URL.revokeObjectURL(logoPreview);
    setLogoFile(null);
    setLogoPreview(null);
    setErrors((p) => ({ ...p, logo: '' }));
  };

  const validateStep = (step) => {
    const newErrors = {};

    if (step === 0) {
      if (!formData.companyName) newErrors.companyName = 'Company name is required';
      if (!formData.industry) newErrors.industry = 'Industry is required';
      if (!formData.companySize) newErrors.companySize = 'Company size is required';
      if (!formData.companyEmail) newErrors.companyEmail = 'Company email is required';
      else if (!validateEmail(formData.companyEmail)) newErrors.companyEmail = 'Please enter a valid email address';
      if (!formData.phone) newErrors.phone = 'Phone number is required';
    }

    if (step === 1) {
      if (!formData.selectedPlan) newErrors.selectedPlan = 'Please select a plan';
    }

    if (step === 2) {
      if (!formData.firstName) newErrors.firstName = 'First name is required';
      if (!formData.lastName) newErrors.lastName = 'Last name is required';
      if (!formData.username) newErrors.username = 'Username is required';
      if (!formData.adminEmail) newErrors.adminEmail = 'Email is required';
      else if (!validateEmail(formData.adminEmail)) newErrors.adminEmail = 'Please enter a valid email address';
      if (!formData.password) newErrors.password = 'Password is required';
      else if (!validatePasswordStrength(formData.password)) {
        newErrors.password = 'Password must be at least 8 characters and include a special character';
      }
      if (formData.password !== formData.confirmPassword) {
        newErrors.confirmPassword = 'Passwords do not match';
      }
      if (!formData.acceptTerms) newErrors.acceptTerms = 'You must accept the terms and conditions';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const nextStep = () => {
    if (validateStep(currentStep)) {
      setCurrentStep((prev) => prev + 1);
    }
  };

  const prevStep = () => {
    setCurrentStep((prev) => prev - 1);
  };

  const handleSubmit = async () => {
    if (!validateStep(currentStep)) return;

    setIsSubmitting(true);
    setErrors({});

    const companyPayload = {
      name: formData.companyName,
      industry: formData.industry,
      company_size: formData.companySize,
      address: formData.address,
      city: formData.city,
      state: formData.state,
      zip_code: formData.zipCode,
      phone: formData.phone,
      company_email: formData.companyEmail,
      website: formData.website,
      plan: formData.selectedPlan,
    };
    const adminUserPayload = {
      first_name: formData.firstName,
      last_name: formData.lastName,
      username: formData.username,
      email: formData.adminEmail,
      password: formData.password,
    };

    try {
      let response;
      if (logoFile) {
        const formDataToSend = new FormData();
        formDataToSend.append('company', JSON.stringify(companyPayload));
        formDataToSend.append('admin_user', JSON.stringify(adminUserPayload));
        formDataToSend.append('logo', logoFile);
        response = await fetch(`${API_BASE}/api/company-registration/`, {
          method: 'POST',
          body: formDataToSend,
        });
      } else {
        response = await fetch(`${API_BASE}/api/company-registration/`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            company: companyPayload,
            admin_user: adminUserPayload,
          }),
        });
      }

      if (response.ok) {
        const result = await response.json();

        // Auto-login with just-registered credentials then go straight to pricing
        try {
          const loginRes = await fetch(`${API_BASE}/api/multi-tenant-login/`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ username: formData.username, password: formData.password }),
          });
          if (loginRes.ok) {
            const loginData = await loginRes.json();
            localStorage.setItem('access_token', loginData.access_token || loginData.access);
            if (loginData.refresh_token || loginData.refresh) {
              localStorage.setItem('refresh_token', loginData.refresh_token || loginData.refresh);
            }
            localStorage.setItem('user', JSON.stringify(loginData.user));
            navigate(`/pricing?plan=${formData.selectedPlan}&registered=true`);
            return;
          }
        } catch (_) {}

        // Fallback: auto-login failed, go to success page
        navigate('/registration-success', {
          state: {
            companyName: formData.companyName,
            username: formData.username,
            companyId: result.company_id,
            userId: result.user_id,
            selectedPlan: formData.selectedPlan,
          },
        });
      } else {
        const errorData = await response.json();
        const errMsg = errorData.error || 'Registration failed. Please try again.';
        const errField = errorData.field;
        if (errField) {
          const fieldMap = {
            companyName: 'companyName',
            companyEmail: 'companyEmail',
            username: 'username',
            adminEmail: 'adminEmail',
            password: 'password',
          };
          const mappedField = fieldMap[errField] || errField;
          setErrors({ [mappedField]: errMsg });
          if (['companyName', 'companyEmail', 'username', 'adminEmail', 'password'].includes(mappedField)) {
            setCurrentStep(mappedField === 'companyName' || mappedField === 'companyEmail' ? 0 : 2);
          }
        } else {
          setErrors({ submit: errMsg });
        }
      }
    } catch (error) {
      setErrors({ submit: 'Network error. Please check your connection and try again.' });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Box sx={{ minHeight: '100vh', bgcolor: 'background.default', py: 6, px: 2 }}>
      <Box sx={{ maxWidth: 800, mx: 'auto' }}>
        <Box sx={{ display: 'flex', justifyContent: 'flex-start', mb: 2 }}>
          <Button
            startIcon={<ArrowBack />}
            onClick={() => navigate('/')}
            sx={{ color: 'text.secondary' }}
          >
            Back to Home
          </Button>
        </Box>

        <Box sx={{ textAlign: 'center', mb: 4 }}>
          <Typography variant="h4" sx={{ fontWeight: 700 }}>
            Register Your Company
          </Typography>
          <Typography variant="body1" color="text.secondary" sx={{ mt: 1 }}>
            Get started with your free trial in just a few steps
          </Typography>
        </Box>

        <Stepper activeStep={currentStep} sx={{ mb: 4 }}>
          {steps.map((step, index) => (
            <Step key={step.label} completed={currentStep > index}>
              <StepLabel>{step.label}</StepLabel>
            </Step>
          ))}
        </Stepper>

        <Paper sx={{ p: 4 }}>
          {currentStep === 0 && (
            <Box>
              <Typography variant="h6" sx={{ fontWeight: 600, mb: 3 }}>
                Company Information
              </Typography>
              <Grid container spacing={3}>
                <Grid item xs={12}>
                  <Typography variant="subtitle2" sx={{ mb: 1 }}>Company Logo (optional)</Typography>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, flexWrap: 'wrap' }}>
                    <Button
                      variant="outlined"
                      component="label"
                      startIcon={<ImageIcon />}
                      disabled={isSubmitting}
                    >
                      Upload Logo
                      <input type="file" hidden accept="image/jpeg,image/png,image/gif,image/webp" onChange={handleLogoChange} />
                    </Button>
                    {logoPreview && (
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <Box component="img" src={logoPreview} alt="Logo preview" sx={{ width: 60, height: 60, objectFit: 'contain', border: '1px solid', borderColor: 'divider', borderRadius: 1 }} />
                        <Typography variant="body2" color="text.secondary">{logoFile?.name}</Typography>
                        <Button size="small" onClick={handleLogoRemove} startIcon={<Delete />} color="error">
                          Remove
                        </Button>
                      </Box>
                    )}
                  </Box>
                  {errors.logo && <Typography variant="caption" color="error" sx={{ display: 'block', mt: 0.5 }}>{errors.logo}</Typography>}
                  <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5 }}>JPEG, PNG, GIF or WebP. Max 2MB.</Typography>
                </Grid>
                <Grid item xs={12} md={6}>
                  <TextField
                    fullWidth
                    label="Company Name *"
                    value={formData.companyName}
                    onChange={(e) => updateFormData('companyName', e.target.value)}
                    disabled={isSubmitting}
                    error={!!errors.companyName}
                    helperText={errors.companyName}
                    placeholder="Enter your company name"
                  />
                </Grid>
                <Grid item xs={12} md={6}>
                  <TextField
                    select
                    fullWidth
                    label="Industry *"
                    value={formData.industry}
                    onChange={(e) => updateFormData('industry', e.target.value)}
                    error={!!errors.industry}
                    helperText={errors.industry}
                  >
                    <MenuItem value="">Select industry</MenuItem>
                    {industries.map((ind) => (
                      <MenuItem key={ind} value={ind}>{ind}</MenuItem>
                    ))}
                  </TextField>
                </Grid>
                <Grid item xs={12} md={6}>
                  <TextField
                    select
                    fullWidth
                    label="Company Size *"
                    value={formData.companySize}
                    onChange={(e) => updateFormData('companySize', e.target.value)}
                    error={!!errors.companySize}
                    helperText={errors.companySize}
                  >
                    <MenuItem value="">Select company size</MenuItem>
                    {companySizes.map((size) => (
                      <MenuItem key={size} value={size}>{size}</MenuItem>
                    ))}
                  </TextField>
                </Grid>
                <Grid item xs={12} md={6}>
                  <TextField
                    fullWidth
                    type="email"
                    label="Company Email *"
                    value={formData.companyEmail}
                    onChange={(e) => updateFormData('companyEmail', e.target.value)}
                    error={!!errors.companyEmail}
                    helperText={errors.companyEmail}
                    placeholder="company@example.com"
                  />
                </Grid>
                <Grid item xs={12} md={6}>
                  <TextField
                    fullWidth
                    type="tel"
                    label="Phone Number *"
                    value={formData.phone}
                    onChange={(e) => updateFormData('phone', e.target.value)}
                    error={!!errors.phone}
                    helperText={errors.phone}
                    placeholder="(555) 123-4567"
                  />
                </Grid>
                <Grid item xs={12} md={6}>
                  <TextField
                    fullWidth
                    type="url"
                    label="Website"
                    value={formData.website}
                    onChange={(e) => updateFormData('website', e.target.value)}
                    placeholder="https://www.example.com"
                  />
                </Grid>
                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    label="Address"
                    value={formData.address}
                    onChange={(e) => updateFormData('address', e.target.value)}
                    placeholder="123 Business St"
                  />
                </Grid>
                <Grid item xs={12} md={4}>
                  <TextField
                    fullWidth
                    label="City"
                    value={formData.city}
                    onChange={(e) => updateFormData('city', e.target.value)}
                    placeholder="City"
                  />
                </Grid>
                <Grid item xs={12} md={4}>
                  <TextField
                    fullWidth
                    label="State"
                    value={formData.state}
                    onChange={(e) => updateFormData('state', e.target.value)}
                    placeholder="State"
                  />
                </Grid>
                <Grid item xs={12} md={4}>
                  <TextField
                    fullWidth
                    label="ZIP Code"
                    value={formData.zipCode}
                    onChange={(e) => updateFormData('zipCode', e.target.value)}
                    placeholder="12345"
                  />
                </Grid>
              </Grid>
            </Box>
          )}

          {currentStep === 1 && (
            <Box>
              <Typography variant="h6" sx={{ fontWeight: 600, mb: 3 }}>
                Choose Your Plan
              </Typography>
              <Grid container spacing={3}>
                {plans.map((plan) => (
                  <Grid item xs={12} md={4} key={plan.id}>
                    <Paper
                      onClick={() => updateFormData('selectedPlan', plan.id)}
                      sx={{
                        p: 2,
                        cursor: 'pointer',
                        border: '2px solid',
                        borderColor: formData.selectedPlan === plan.id ? 'primary.main' : 'divider',
                        bgcolor: formData.selectedPlan === plan.id ? 'primary.50' : 'background.paper',
                        ...(plan.popular && { outline: '2px solid', outlineColor: 'primary.main', outlineOffset: -2 }),
                      }}
                    >
                      {plan.popular && (
                        <Typography variant="caption" sx={{ color: 'primary.main', fontWeight: 600 }}>
                          Most Popular
                        </Typography>
                      )}
                      <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>{plan.name}</Typography>
                      <Typography variant="body2" color="text.secondary">{plan.description}</Typography>
                      <Box sx={{ mt: 1 }}>
                        <Typography component="span" variant="h6">{plan.price}</Typography>
                        <Typography component="span" variant="body2" color="text.secondary">{plan.period}</Typography>
                      </Box>
                      <Box component="ul" sx={{ pl: 2, mt: 2, listStyle: 'none' }}>
                        {plan.features.map((f, i) => (
                          <Box key={i} component="li" sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
                            <Check sx={{ fontSize: 16, color: 'success.main' }} />
                            <Typography variant="body2">{f}</Typography>
                          </Box>
                        ))}
                      </Box>
                    </Paper>
                  </Grid>
                ))}
              </Grid>
              {errors.selectedPlan && (
                <Typography variant="body2" color="error" sx={{ mt: 2 }}>{errors.selectedPlan}</Typography>
              )}
            </Box>
          )}

          {currentStep === 2 && (
            <Box>
              <Typography variant="h6" sx={{ fontWeight: 600, mb: 1 }}>
                Create Your Super Admin Account
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
                This will be your main account with full administrative privileges for your company.
              </Typography>
              <Grid container spacing={3}>
                <Grid item xs={12} md={6}>
                  <TextField
                    fullWidth
                    label="First Name *"
                    value={formData.firstName}
                    onChange={(e) => updateFormData('firstName', e.target.value)}
                    error={!!errors.firstName}
                    helperText={errors.firstName}
                    placeholder="John"
                  />
                </Grid>
                <Grid item xs={12} md={6}>
                  <TextField
                    fullWidth
                    label="Last Name *"
                    value={formData.lastName}
                    onChange={(e) => updateFormData('lastName', e.target.value)}
                    error={!!errors.lastName}
                    helperText={errors.lastName}
                    placeholder="Doe"
                  />
                </Grid>
                <Grid item xs={12} md={6}>
                  <TextField
                    fullWidth
                    label="Username *"
                    value={formData.username}
                    onChange={(e) => updateFormData('username', e.target.value)}
                    error={!!errors.username}
                    helperText={errors.username}
                    placeholder="johndoe"
                  />
                </Grid>
                <Grid item xs={12} md={6}>
                  <TextField
                    fullWidth
                    type="email"
                    label="Email *"
                    value={formData.adminEmail}
                    onChange={(e) => updateFormData('adminEmail', e.target.value)}
                    error={!!errors.adminEmail}
                    helperText={errors.adminEmail}
                    placeholder="john@example.com"
                  />
                </Grid>
                <Grid item xs={12} md={6}>
                  <TextField
                    fullWidth
                    type="password"
                    label="Password *"
                    value={formData.password}
                    onChange={(e) => updateFormData('password', e.target.value)}
                    error={!!errors.password}
                    helperText={errors.password || 'Min 8 characters, include a special character'}
                    placeholder="••••••••"
                  />
                </Grid>
                <Grid item xs={12} md={6}>
                  <TextField
                    fullWidth
                    type="password"
                    label="Confirm Password *"
                    value={formData.confirmPassword}
                    onChange={(e) => updateFormData('confirmPassword', e.target.value)}
                    error={!!errors.confirmPassword}
                    helperText={errors.confirmPassword}
                    placeholder="••••••••"
                  />
                </Grid>
                <Grid item xs={12}>
                  <FormControl error={!!errors.acceptTerms} component="fieldset">
                    <FormControlLabel
                      control={
                        <Checkbox
                          checked={formData.acceptTerms}
                          onChange={(e) => updateFormData('acceptTerms', e.target.checked)}
                          color="primary"
                        />
                      }
                      label={
                        <Typography variant="body2">
                          I agree to the Terms of Service and Privacy Policy *
                        </Typography>
                      }
                    />
                    {errors.acceptTerms && (
                      <Typography variant="caption" color="error">{errors.acceptTerms}</Typography>
                    )}
                  </FormControl>
                </Grid>
                <Grid item xs={12}>
                  <FormControlLabel
                    control={
                      <Checkbox
                        checked={formData.acceptMarketing}
                        onChange={(e) => updateFormData('acceptMarketing', e.target.checked)}
                        color="primary"
                      />
                    }
                    label={
                      <Typography variant="body2">
                        I would like to receive marketing communications about product updates and features
                      </Typography>
                    }
                  />
                </Grid>
              </Grid>
            </Box>
          )}

          {errors.submit && (
            <Alert severity="error" sx={{ mt: 3 }} onClose={() => setErrors((p) => ({ ...p, submit: '' }))}>
              {errors.submit}
            </Alert>
          )}

          <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 4 }}>
            <Button
              startIcon={<ArrowBack />}
              onClick={prevStep}
              disabled={currentStep === 0 || isSubmitting}
              variant="outlined"
            >
              Previous
            </Button>
            {currentStep < 2 ? (
              <Button endIcon={<ArrowForward />} onClick={nextStep} disabled={isSubmitting} variant="contained">
                Next
              </Button>
            ) : (
              <Button
                endIcon={isSubmitting ? <CircularProgress size={20} color="inherit" /> : <ArrowForward />}
                onClick={handleSubmit}
                disabled={isSubmitting}
                variant="contained"
              >
                {isSubmitting ? 'Creating Account...' : 'Create Account'}
              </Button>
            )}
          </Box>
        </Paper>

        <Typography variant="body2" align="center" sx={{ mt: 3, color: 'text.secondary' }}>
          Already have an account?{' '}
          <Link
            component="button"
            variant="body2"
            onClick={() => navigate('/login')}
            sx={{ fontWeight: 600 }}
          >
            Sign in
          </Link>
        </Typography>
      </Box>
    </Box>
  );
};

export default CompanyRegistration;
